<template>
    <app-framework>
      <header class="container">
        <bread-crumbs :list="[
          { name: $t('home'), link: '/' },
          { name: $t('menu_listing'), link: '/menus' },
          { name: $t($route.params.id === 'new' ? 'menu_form_new' : 'menu_form_edit') }
        ]" />
        <h1>{{ $t($route.params.id === 'new' ? 'menu_form_new' : 'menu_form_edit') }}</h1>
      </header>
      <article class="container framework-slot">
        <div v-if="menu.loading">
            <loading-state/>
        </div>
        <div v-else-if="menu.error" class="alert alert-danger" role="alert">
            {{ $t('failed_retrieve_data_from_server') }}
            <button class="btn btn-link" @click="tryAgain">{{ $t('try_again') }}</button>
        </div>
        <form v-else v-on:submit.prevent="saveMenu">
            <div v-if="validationError" class="alert alert-danger" role="alert">
                {{validationError}}
            </div>
            <form-page>
                <div class="form-row">
                    <form-group class="col-12 required" :label="$t('name')" label-for="name" prepend-icon="tag"
                                :invalid-feedback="validationErrors.name">
                        <input id="name" autocomplete="name" type="text" class="form-control"
                               v-model="menu.node.name" v-focus>
                    </form-group>
                </div>
            </form-page>
            <footer>
                <div class="form-row">
                    <div class="col-auto">
                        <button :disabled="menu.saving" class="btn btn-primary"
                                v-text="!menu.saving ? $t('save_button') : $t('saving')"></button>
                    </div>
                </div>
            </footer>
        </form>
      </article>
    </app-framework>
</template>

<script>
    import FormMixin from "../../mixins/FormMixin";
    import AppFramework from "../../components/layout/AppFramework";
    import FormPage from "../../components/common/FormPage";
    import FormGroup from "../../components/common/FormGroup";
    import LoadingState from "../../components/states/LoadingState";
    import gql from "graphql-tag";
    import BreadCrumbs from "@/components/layout/BreadCrumbs";

    export default {
        name: 'menuForm',
        i18nOptions: {namespaces: ['menu_form', 'common', 'breadcrumbs', 'error']},
        components: {BreadCrumbs, LoadingState, FormGroup, FormPage, AppFramework},
        mixins: [FormMixin],
        metaInfo() {
          return {
            title: this.$t(this.$route.params.id === 'new' ? 'menu_form_new' : 'menu_form_edit')
          }
        },
        data: () => ({
            menu: {
                node: null,
                loading: true,
                error: null,
                saving: false
            }
        }),
        computed: {
            isNew: function () {
                return this.$route.params.id === "new";
            },
        },
        async mounted() {
            await this.queryMenu();
        },
        methods: {
            async tryAgain() {
                if (!this.isNew && this.menu.error) {
                    await this.queryMenu();
                }
            },
            async queryMenu() {
                if (this.isNew) {
                    this.menu.node = {};
                    this.menu.loading = false;
                    return;
                }

                this.menu.loading = true;
                try {
                    const response = await this.$apolloClient.query({
                        query: gql`query ($id: ID!) {
                            menu(id: $id) {
                                id
                                name
                                isDeleted
                            }
                        }`,
                        variables: {
                            id: this.$route.params.id
                        },
                        fetchPolicy: "network-only",
                    });
                    this.menu.node = response.data.menu;

                    this.menu.error = null;
                } catch (e) {
                    this.menu.error = e.message
                }
                this.menu.loading = false;
            },
            async saveMenu() {
                if (this.menu.saving || !this.checkForm()) {
                    return;
                }
                this.disableForm();
                this.menu.saving = true;
                try {
                    if (this.isNew) {
                        await this.createMenu();
                    } else {
                        await this.updateMenu();
                    }
                    await this.$apolloClient.resetStore();
                    await this.$router.push('/menus');
                } catch (e) {
                    this.menu.saving = false;
                    this.activeForm();
                    this.showError(e);
                }
            },
            async createMenu() {
                await this.$apolloClient.mutate({
                    mutation: gql`mutation ($input: MenuInput!) {
                        createMenu(
                            input: $input
                        ) {
                            id
                            name
                            isDeleted
                        }
                    }`,
                    variables: {
                        input: {
                            name: this.menu.node.name
                        }
                    }
                });
            },
            async updateMenu() {
                await this.$apolloClient.mutate({
                    mutation: gql`mutation ($id: ID! $input: MenuInput!) {
                        updateMenu(
                            id: $id
                            input: $input
                        ) {
                            id
                            name
                        }
                    }`,
                    variables: {
                        id: this.$route.params.id,
                        input: {
                            name: this.menu.node.name
                        },
                    }
                });
            },
            validateForm() {
                this.validateFormMixin();
            }
        }
    }
</script>
